import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { Sparkle } from "./Sparkle";

export const Hero = () => {
    const { t } = useLanguage();
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative overflow-hidden"
        >
            <div className="max-w-[1320px] mx-auto px-6 lg:px-12 pt-10 lg:pt-16 pb-14 lg:pb-20">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    {/* Left: Portrait */}
                    <div className="lg:col-span-5 rise rise-1">
                        <div className="relative">
                            <div
                                className="absolute -top-8 -left-6 w-[78%] h-[78%] rounded-[42%_58%_64%_36%/52%_48%_52%_48%]"
                                style={{
                                    background:
                                        "linear-gradient(140deg, var(--peach) 0%, var(--peach-soft) 100%)",
                                    filter: "blur(0.5px)",
                                }}
                            />
                            <div
                                className="relative aspect-[4/5] rounded-[28px] overflow-hidden grain-overlay"
                                style={{ background: "var(--cream-deep)" }}
                            >
                                <img
                                    src="https://customer-assets.emergentagent.com/job_portfolio-live-55/artifacts/4oo43pid_Screenshot%202022-06-02%20at%2013.55%201%201.png"
                                    alt="Portrait of Charlyn De Wulf"
                                    className="w-full h-full object-cover"
                                    data-testid="hero-portrait"
                                />
                                <div
                                    className="absolute bottom-5 left-5 right-5 flex items-center justify-between font-mono-archive text-[10px]"
                                    style={{ color: "var(--paper)" }}
                                >
                                </div>
                            </div>

                            <div
                                className="absolute -bottom-7 -right-4 w-[110px] h-[110px] rounded-full flex items-center justify-center spin-slow"
                                style={{
                                    background: "var(--ink)",
                                    color: "var(--paper)",
                                }}
                            >
                                <svg
                                    viewBox="0 0 100 100"
                                    className="absolute inset-0 w-full h-full"
                                >
                                    <defs>
                                        <path
                                            id="circle"
                                            d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0"
                                        />
                                    </defs>
                                    <text
                                        fill="currentColor"
                                        fontFamily="Manrope, sans-serif"
                                        fontSize="8.5"
                                        letterSpacing="2.4"
                                    >
                                        <textPath href="#circle">
                                            {t("hero.badge.open")}
                                        </textPath>
                                    </text>
                                </svg>
                                <span
                                    className="font-serif-display text-[26px] italic flex items-center justify-center"
                                    style={{ color: "var(--peach)" }}
                                >
                                    <Sparkle size={22} color="var(--peach)" />
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Right: Copy */}
                    <div className="lg:col-span-7">
                        <div className="rise rise-1 flex items-center gap-3 mb-7">
                            <span
                                className="inline-block w-2 h-2 rounded-full pulse-dot"
                                style={{ background: "var(--moss)" }}
                            />
                            <span
                                className="font-mono-archive text-[11px]"
                                style={{ color: "var(--muted-ink)" }}
                            >
                                {t("hero.eyebrow")}
                            </span>
                        </div>

                        <h1
                            className="rise rise-2 font-serif-display text-[44px] sm:text-[64px] lg:text-[88px] leading-[0.96]"
                            style={{ color: "var(--ink)" }}
                            data-testid="hero-headline"
                        >
                            {t("hero.title.a")}
                            <br />
                            {t("hero.title.b")}{" "}
                            <span
                                className="italic"
                                style={{ color: "var(--terracotta)" }}
                            >
                                {t("hero.title.c")}.
                            </span>
                        </h1>

                        <p
                            className="rise rise-3 mt-8 max-w-[580px] text-[17px] leading-[1.65]"
                            style={{ color: "var(--ink-soft)" }}
                        >
                            <strong className="font-semibold">
                                {t("hero.bio.role")}
                            </strong>{" "}
                            {t("hero.bio.text")}
                        </p>

                        {/* Soft-skill chips */}
                        <div className="rise rise-3 mt-7 flex flex-wrap gap-2 max-w-[620px]">
                            {[
                                "Empathy",
                                "Communication",
                                "Autonomy",
                                "Curiosity",
                                "Problem solver",
                                "Creative thinker",
                                "Straight to the point",
                                "Outside the box",
                                "Goal driven",
                            ].map((s) => (
                                <span
                                    key={s}
                                    data-testid={`soft-skill-${s.replace(/\s+/g, "-").toLowerCase()}`}
                                    className="text-[11.5px] font-medium px-3 py-1.5 rounded-full"
                                    style={{
                                        color: "var(--ink-soft)",
                                        background: "rgba(255,255,255,0.5)",
                                        fontFamily: "Manrope, sans-serif",
                                    }}
                                >
                                    {s}
                                </span>
                            ))}
                        </div>

                        <div className="rise rise-4 mt-10 flex flex-wrap items-center gap-4">
                            <a
                                href="#cases"
                                data-testid="hero-cta-archive"
                                className="pill pill-primary"
                            >
                                {t("hero.cta.cases")}
                                <span aria-hidden>↗</span>
                            </a>
                            <a
                                href="#skills"
                                data-testid="hero-cta-look"
                                className="font-serif-display italic text-[22px] archive-link"
                                style={{ color: "var(--ink)" }}
                            >
                                {t("hero.cta.skills")}
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Marquee */}
            <div
                className="overflow-hidden"
                style={{
                    background: "var(--paper)",
                }}
            >
                <div className="marquee-track flex whitespace-nowrap py-5">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <div
                            key={i}
                            className="flex items-center gap-12 px-8 font-serif-display italic text-[28px]"
                            style={{ color: "var(--ink)" }}
                        >
                            <span>Empathy</span>
                            <span style={{ color: "var(--yellow-deep)" }}><Sparkle size={18} color="var(--yellow-deep)" /></span>
                            <span>Curiosity</span>
                            <span style={{ color: "var(--yellow-deep)" }}><Sparkle size={18} color="var(--yellow-deep)" /></span>
                            <span>Problem solving</span>
                            <span style={{ color: "var(--yellow-deep)" }}><Sparkle size={18} color="var(--yellow-deep)" /></span>
                            <span>Autonomy</span>
                            <span style={{ color: "var(--yellow-deep)" }}><Sparkle size={18} color="var(--yellow-deep)" /></span>
                            <span>Cross-functional</span>
                            <span style={{ color: "var(--yellow-deep)" }}><Sparkle size={18} color="var(--yellow-deep)" /></span>
                            <span>Outside the box</span>
                            <span style={{ color: "var(--yellow-deep)" }}><Sparkle size={18} color="var(--yellow-deep)" /></span>
                            <span>Goal driven</span>
                            <span style={{ color: "var(--yellow-deep)" }}><Sparkle size={18} color="var(--yellow-deep)" /></span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
